import { everAffordable } from '../content/economy';
import type { MaterialId } from '../content/materials';
import type { GateFlags, SimState } from './state';

/**
 * The three ways progression can be gated, replacing the cash-only `everAffordable(peakCash, …)`
 * pattern — a `cash`-only gate has nothing to say about content with no cash cost of its own
 * (e.g. unlocking `filter` once slag has actually cost the player money) or content that should
 * follow directly from an action rather than a balance (e.g. unlocking `from` once `filter` has
 * ever been placed).
 */
export type GateCondition =
  | { kind: 'cash'; threshold: number }
  | { kind: 'production'; material: MaterialId; count: number }
  | { kind: 'flag'; flag: keyof GateFlags }
  /** Satisfied by whichever branch lands first — lets content reward a specific action without
   * making it the only way through, so skipping that action delays progress instead of ending it. */
  | { kind: 'any'; conditions: readonly GateCondition[] };

export function isGateSatisfied(state: SimState, condition: GateCondition): boolean {
  switch (condition.kind) {
    case 'cash':
      return everAffordable(state.economy.peakCash, condition.threshold);
    case 'production':
      return (state.economy.materialSaleCounts[condition.material] ?? 0) >= condition.count;
    case 'flag':
      return state.flags[condition.flag];
    case 'any':
      return condition.conditions.some((inner) => isGateSatisfied(state, inner));
  }
}
